import { useMemo } from 'react'

import {
  AnalyticsLevel,
  ResolutionRate,
  ResponseCommentDensity,
} from '@/schema'

import { ViewLevel } from '../constants'
import { useDashboardStore } from '../store'

import {
  useGetCommentDensity,
  useGetResolutionMetrics,
  useGetReviewCycleTime,
  useGetTflCompletionRate,
  useGetTimeToFirstComment,
} from './index'

type TUseDeliverableLevelResult = {
  commentDensityMetric: ResponseCommentDensity
  resolutionMetric?: ResolutionRate
  resolutionRate?: number
  openCommentsCount?: number
  resolvedCommentsCount?: number
  reviewCycleTime?: number
  tflCompletionRate?: number
  approvedFiles?: number
  totalFiles?: number
  timeToFirstComment?: number
  isLoadingCommentDensity: boolean
  isLoadingResolutionMetrics: boolean
  isLoadingReviewCycleTime: boolean
  isLoadingTflCompletionRate: boolean
  isLoadingTimeToFirstComment: boolean
}

export const useDeliverableLevel = (): TUseDeliverableLevelResult => {
  const { filters, viewLevel } = useDashboardStore()

  const isDeliverableLevel = viewLevel === ViewLevel.DELIVERABLE_SETS

  const shouldFetch =
    isDeliverableLevel &&
    !!filters.companyId &&
    !!filters.projectId &&
    !!filters.deliverableSetId

  // Shared query options to avoid duplication
  const queryOptions = useMemo(
    () => ({
      query: {
        staleTime: 1000,
        enabled: shouldFetch,
      },
    }),
    [shouldFetch]
  )

  const {
    data: { data: [commentDensityMetric] = [] } = {},
    isLoading: isLoadingCommentDensity,
  } = useGetCommentDensity(
    {
      level: AnalyticsLevel.deliverable_set,
      company_id: filters.companyId,
      project_id: filters.projectId,
      deliverable_set_id: filters.deliverableSetId,
    },
    queryOptions
  )

  const {
    data: { data: [resolutionMetricsResponse] = [] } = {},
    isLoading: isLoadingResolutionMetrics,
  } = useGetResolutionMetrics(
    {
      level: AnalyticsLevel.deliverable_set,
      deliverable_set_id: filters.deliverableSetId,
    },
    queryOptions
  )

  const {
    data: { data: [reviewCycleTimeData] = [] } = {},
    isLoading: isLoadingReviewCycleTime,
  } = useGetReviewCycleTime(
    {
      deliverable_set_id: filters.deliverableSetId || '',
    },
    queryOptions
  )

  const {
    data: { data: [tflCompletionRateData] = [] } = {},
    isLoading: isLoadingTflCompletionRate,
  } = useGetTflCompletionRate(
    {
      deliverable_set_id: filters.deliverableSetId || '',
    },
    queryOptions
  )

  const {
    data: { data: [timeToFirstCommentData] = [] } = {},
    isLoading: isLoadingTimeToFirstComment,
  } = useGetTimeToFirstComment(
    {
      deliverable_set_id: filters.deliverableSetId || '',
    },
    queryOptions
  )

  const resolutionMetric = resolutionMetricsResponse?.data?.[0]
  const resolutionRate = resolutionMetricsResponse?.metric
  const openCommentsCount = resolutionMetric?.open_count
  const resolvedCommentsCount = resolutionMetric?.resolved_count
  const reviewCycleTime = reviewCycleTimeData?.metric
  const tflCompletionRate = tflCompletionRateData?.metric
  const approvedFiles = tflCompletionRateData?.data?.[0]?.approved_files
  const totalFiles = tflCompletionRateData?.data?.[0]?.total_files
  const timeToFirstComment = timeToFirstCommentData?.metric

  return {
    commentDensityMetric,
    isLoadingCommentDensity,
    resolutionMetric,
    resolutionRate,
    openCommentsCount,
    resolvedCommentsCount,
    reviewCycleTime,
    isLoadingResolutionMetrics,
    isLoadingReviewCycleTime,
    tflCompletionRate,
    approvedFiles,
    totalFiles,
    isLoadingTflCompletionRate,
    timeToFirstComment,
    isLoadingTimeToFirstComment,
  }
}
